import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import PrintIcon from '@mui/icons-material/Print'

/**
 * Component to select which pages to print
 * Allows selecting individual pages or all pages
 */
export default function PageSelectionMenu({ totalPages, onPrint, disabled = false }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedPages, setSelectedPages] = useState(() => {
    // Initialize with all pages selected
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  })

  const open = Boolean(anchorEl)

  // Update selected pages when totalPages changes
  useEffect(() => {
    if (totalPages > 0) {
      setSelectedPages((prev) => {
        const maxPage = Math.max(...prev, 0)
        if (maxPage > totalPages) {
          // If total pages decreased, remove pages that no longer exist
          return prev.filter((page) => page <= totalPages)
        } else if (maxPage < totalPages) {
          // If total pages increased, add new pages
          const newPages = Array.from(
            { length: totalPages - maxPage },
            (_, i) => maxPage + i + 1,
          )
          return [...prev, ...newPages]
        }
        return prev
      })
    }
  }, [totalPages])

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const togglePage = (pageNumber) => {
    setSelectedPages((prev) => {
      if (prev.includes(pageNumber)) {
        return prev.filter((p) => p !== pageNumber)
      } else {
        return [...prev, pageNumber].sort((a, b) => a - b)
      }
    })
  }

  const selectAll = () => {
    setSelectedPages(Array.from({ length: totalPages }, (_, i) => i + 1))
  }

  const deselectAll = () => {
    setSelectedPages([])
  }

  const handlePrint = () => {
    if (selectedPages.length === 0) {
      alert('Please select at least one page to print.')
      return
    }
    handleClose()
    onPrint(selectedPages)
  }

  const selectedCount = selectedPages.length
  const allSelected = selectedPages.length === totalPages && totalPages > 0
  const noneSelected = selectedPages.length === 0

  return (
    <>
      <Tooltip title="Select pages to print">
        <span>
          <IconButton
            onClick={handleClick}
            disabled={disabled || totalPages === 0}
            sx={{
              color: '#4338ca',
              '&:hover': {
                backgroundColor: 'rgba(67, 56, 202, 0.08)',
              },
            }}
          >
            <FilterListIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: '280px',
            maxWidth: '320px',
            maxHeight: '400px',
            mt: 1,
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(67, 56, 202, 0.15)',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(67, 56, 202, 0.1)' }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: '#1e293b',
              mb: 1,
              fontSize: '14px',
            }}
          >
            Select Pages to Print
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '12px' }}>
            {selectedCount} of {totalPages} page{totalPages !== 1 ? 's' : ''} selected
          </Typography>
        </Box>

        <Box sx={{ p: 1, borderBottom: '1px solid rgba(67, 56, 202, 0.1)' }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={selectAll}
              disabled={allSelected}
              sx={{
                textTransform: 'none',
                fontSize: '12px',
                borderColor: 'rgba(67, 56, 202, 0.3)',
                color: '#4338ca',
                '&:hover': {
                  borderColor: '#4338ca',
                  backgroundColor: 'rgba(67, 56, 202, 0.08)',
                },
              }}
            >
              Select All
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={deselectAll}
              disabled={noneSelected}
              sx={{
                textTransform: 'none',
                fontSize: '12px',
                borderColor: 'rgba(67, 56, 202, 0.3)',
                color: '#4338ca',
                '&:hover': {
                  borderColor: '#4338ca',
                  backgroundColor: 'rgba(67, 56, 202, 0.08)',
                },
              }}
            >
              Deselect All
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            maxHeight: '200px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(67, 56, 202, 0.05)',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(67, 56, 202, 0.2)',
              borderRadius: '3px',
              '&:hover': {
                background: 'rgba(67, 56, 202, 0.3)',
              },
            },
          }}
        >
          {Array.from({ length: totalPages }, (_, i) => {
            const pageNumber = i + 1
            const isSelected = selectedPages.includes(pageNumber)
            return (
              <MenuItem
                key={pageNumber}
                onClick={() => togglePage(pageNumber)}
                sx={{
                  py: 0.5,
                  px: 1.5,
                  '&:hover': {
                    backgroundColor: 'rgba(67, 56, 202, 0.05)',
                  },
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      sx={{
                        color: '#4338ca',
                        '&.Mui-checked': {
                          color: '#4338ca',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#1e293b' }}>
                      Page {pageNumber}
                    </Typography>
                  }
                  sx={{ margin: 0 }}
                />
              </MenuItem>
            )
          })}
        </Box>

        <Box sx={{ p: 1.5, borderTop: '1px solid rgba(67, 56, 202, 0.1)' }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={noneSelected}
            sx={{
              textTransform: 'none',
              backgroundColor: '#4338ca',
              '&:hover': {
                backgroundColor: '#3730a3',
              },
              '&:disabled': {
                backgroundColor: 'rgba(67, 56, 202, 0.3)',
                color: 'rgba(255, 255, 255, 0.5)',
              },
            }}
          >
            Print {selectedCount > 0 && `(${selectedCount})`}
          </Button>
        </Box>
      </Menu>
    </>
  )
}

